import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Input, SegmentedControl, Sheet } from '@astral/ui';
import {
  formatPackTitle,
  PACK_TITLE_MAX,
  stripPackBrand,
  type PackBrandingPlacement,
} from '@astral/shared';
import { api, ApiClientError } from '../lib/api.js';
import { useT } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';

export interface EditPackNameSheetProps {
  open: boolean;
  projectId: string;
  /** The project's UNBRANDED base title. */
  title: string;
  /** Effective placement to preview and (when editable) to change. */
  branding: PackBrandingPlacement;
  /** Whether this account may choose — Premium/Premium Plus, ADMIN or OWNER. */
  canChooseBranding: boolean;
  /**
   * The title the Telegram set currently carries, when it could be read. Shown
   * verbatim so a pack still on the retired ﹙@AstralAtelier﹚ tag is legible
   * before the rename migrates it.
   */
  remoteTitle?: string | null;
  onSaved: (result: { title: string; branding: PackBrandingPlacement }) => void;
  onClose: () => void;
}

/**
 * Rename an ALREADY-PUBLISHED pack.
 *
 * Deliberately the same shape as the first-publish sheet — one base-title field,
 * the live branded preview, and the same three-way placement control — so there
 * is nothing new to learn and the project screen is untouched. The tag is never
 * typed into the field: the base stays unbranded and the placement is chosen,
 * exactly as at publish time.
 *
 * A Free or lapsed account sees the preview (always tagged) with no control. The
 * server enforces that regardless, so the missing control is a statement about
 * the UI, not the security boundary.
 */
export function EditPackNameSheet({
  open,
  projectId,
  title,
  branding,
  canChooseBranding,
  remoteTitle,
  onSaved,
  onClose,
}: EditPackNameSheetProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const [draft, setDraft] = useState(title);
  const [placement, setPlacement] = useState<PackBrandingPlacement>(branding);
  const [busy, setBusy] = useState(false);

  // Re-seed each time the sheet opens, so a cancelled edit is never carried
  // into the next one and the controls always start from the saved truth.
  useEffect(() => {
    if (!open) return;
    setDraft(title);
    setPlacement(branding);
  }, [open, title, branding]);

  /*
   * The exact string the server will send to Telegram, computed with the SAME
   * formatter the backend uses — so the preview cannot disagree with the result,
   * including when the base has to be shortened to keep the tag whole.
   */
  const preview = useMemo(
    () => formatPackTitle(draft.trim() || title, placement),
    [draft, placement, title],
  );
  const trimmed = useMemo(
    () => Array.from(stripPackBrand(preview)).length < Array.from(stripPackBrand(draft.trim())).length,
    [preview, draft],
  );

  const save = async (): Promise<void> => {
    const base = draft.trim();
    if (base.length === 0) return;
    setBusy(true);
    try {
      const result = await api.editPackTitle(projectId, {
        title: base,
        // Only an entitled account states a placement; otherwise the server's
        // own resolution stands and nothing is overwritten.
        ...(canChooseBranding ? { packBranding: placement } : {}),
      });
      onSaved({ title: result.title, branding: result.branding });
      toast.show(t('packName.saved'), 'success');
      onClose();
    } catch (err) {
      /*
       * A failed Telegram write must never read as success. The server's own
       * message is shown for the failures it states deliberately (a rejected
       * title, Telegram unreachable); anything else falls back to plain copy
       * rather than leaking an internal error.
       */
      const message =
        err instanceof ApiClientError &&
        ['UNPROCESSABLE', 'VALIDATION_ERROR', 'CONFLICT', 'UPSTREAM_UNAVAILABLE', 'RATE_LIMITED'].includes(err.code)
          ? err.message
          : t('packName.failed');
      toast.show(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t('packName.title')}
      footer={
        <Button
          fullWidth
          size="lg"
          loading={busy}
          disabled={busy || draft.trim().length === 0}
          leftIcon={<Icon name="check" size={18} />}
          onClick={() => void save()}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="stack" style={{ gap: 16 }}>
        {remoteTitle && (
          <div className="stack" style={{ gap: 4 }}>
            <span className="muted" style={{ fontSize: 12 }}>{t('packName.currentLabel')}</span>
            <div className="packlink-preview">{remoteTitle}</div>
          </div>
        )}

        <div className="stack" style={{ gap: 6 }}>
          <Input
            label={t('packName.nameLabel')}
            value={draft}
            maxLength={PACK_TITLE_MAX}
            onChange={(event) => setDraft(event.target.value)}
          />
          <span className="muted" style={{ fontSize: 12 }}>{t('packName.newLabel')}</span>
          <div className="packlink-preview">{preview}</div>
          {trimmed && (
            <span className="packlink-bad" style={{ fontSize: 12 }}>{t('packName.trimmed')}</span>
          )}

          {canChooseBranding && (
            <div className="stack" style={{ gap: 6, marginTop: 6 }}>
              <span className="muted" style={{ fontSize: 12 }}>{t('packLink.branding.label')}</span>
              <SegmentedControl<PackBrandingPlacement>
                value={placement}
                onChange={setPlacement}
                options={[
                  { value: 'NONE', label: t('packLink.branding.none') },
                  { value: 'START', label: t('packLink.branding.start') },
                  { value: 'END', label: t('packLink.branding.end') },
                ]}
              />
            </div>
          )}
        </div>

        <p className="muted" style={{ fontSize: 12, margin: 0 }}>{t('packName.hint')}</p>
      </div>
    </Sheet>
  );
}
